import { FC, useMemo } from 'react';

import { Link } from '@dbt-labs/sourdough';

import { Spinner } from '@/components/Spinner';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useMeshProjects } from '@/hooks/discovery/meshProjects';
import { ProducerProjectsData } from '@/hooks/discovery/relatedProjects';
import { useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { ProjectPanelUpstreamRelationshipLinks } from './ProjectPanelUpstreamRelationshipLinks';

interface PanelDetailsProps {
  projectId: number | null;
  upstreamRelationships: ProducerProjectsData | null;
}

// If we're already passing in `upstreamRelationships` from the parent to minimize business logic here,
// would it make more sense to make this component purely display, i.e. remove all business logic and hooks
// and just have the parent component pass it down?
export const ProjectPanelRelationshipsTab: FC<PanelDetailsProps> = ({
  projectId,
  upstreamRelationships,
}) => {
  const { accountId } = useDbtCloudAccount();
  const getTargetEnvironmentType = useTargetEnvironmentType();

  const projectOverviewLink = useLinkToExplorer('project');
  const [projectQuery] = useMeshProjects({
    accountId,
    projectId,
  });

  // TODO: need to fork logic for project lineage where we see consumerProjects of an environment
  // TODO: we need to test this with a project A -> project B -> project C
  const importedBy = useMemo(() => {
    if (!projectQuery.data) {
      return null;
    }
    const project = projectQuery.data?.account.meshProjects?.find(
      (project) => project.projectId === projectId,
    );

    return project?.dependentProjects;
  }, [projectQuery.data, projectId]);

  // TODO: do we need to include !upstreamRelationships
  if (!importedBy || !accountId || !projectId) {
    return (
      <div className="mt-4">
        <Spinner />
      </div>
    );
  }

  const hasDependentProjects = !!importedBy?.length;

  return (
    <>
      <>
        {hasDependentProjects && (
          <>
            <div className="mt-4 text-xs font-medium text-fgAlt">Imported By</div>
            <div className="mt-4">
              <ul className="space-y-2">
                {importedBy.map((x, index) => {
                  return (
                    <li
                      key={x.projectId}
                      className={`-mx-2 px-2 py-1 ${index % 2 === 1 ? 'bg-bgBackground' : ''}`}
                    >
                      <Link
                        isInternal={true}
                        to={projectOverviewLink({
                          accountId,
                          projectId: x.projectId,
                          environmentType: getTargetEnvironmentType(
                            x.defaultEnvironmentId,
                          ),
                        })}
                        className="text-sm font-normal"
                      >
                        {x.dbtCloudProject || x.dbtCoreProject}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
        {!hasDependentProjects && (
          <div className="mt-4 italic text-fgDecorative">
            Not imported by any projects
          </div>
        )}
      </>
      <ProjectPanelUpstreamRelationshipLinks
        accountId={accountId}
        header="References"
        emptyResultsText="Not referencing any public models"
        links={upstreamRelationships?.references || []}
        testId="project-panel-upstream-relationships-references"
      />
    </>
  );
};
